import { useDispatch, useSelector } from 'react-redux';
import {
  setListing,
  setSelected,
  setFilters,
  setSelectedFilters,
  setNextToken,
} from '../store/ducks/messaging';
import { HeadCell } from '../models/dataTable';
import useApp from './useApp';
import {
  CreateVariables,
  ConceptGetVariables,
  ConceptListingVariables,
  Option,
} from '../models/app';
import { getMessaging, listMessagings } from '../graphql/queries';
import { generateClient } from '@aws-amplify/api';
import { createMessaging, updateMessaging } from '../graphql/mutations';
import {
  CreateMessageInput,
  CreateMessagingInput,
  UpdateMessagingInput,
} from '../API';

const client = generateClient();

// These will be auto-generated from the schema
// For now, we'll use dynamic queries
const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();
  const listing = useSelector(
    (state: any) => state[`${listingName}`]?.['listing'] || [],
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]?.['nextToken'] || null,
  );

  const conceptsSelected = useSelector((state: any) => state.concepts.selected);
  const conceptsSelectedFilters = useSelector(
    (state: any) => state.concepts.selectedFilters,
  );

  async function fetch(params: ConceptListingVariables, isAuth = true) {
    const { accountID, conceptID } = params;

    try {
      // Filter Section
      let and: any[] = [];

      const filter: any = {
        deleted: { eq: '0' },
      };

      // Concepts Filter
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        let or: any[] = [];
        for (let concept of conceptsSelectedFilters) {
          or.push({ conceptId: { eq: concept.id } });
        }

        and.push({ or: or });
      } else {
        filter.conceptId = { eq: conceptID };
      }

      // Dynamic query - will be replaced with auto-generated query after schema push
      const messagingListing: any = await client.graphql({
        query: listMessagings,
        variables: {
          filter,
          limit: 100000,
        },
        authMode: isAuth ? 'userPool' : 'iam',
      });

      const currentNextToken = messagingListing.data.listMessagings.nextToken;
      const listing: any = messagingListing.data.listMessagings.items;

      dispatch(setNextToken(currentNextToken));
      dispatch(setListing(listing));
      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function get(params: ConceptGetVariables) {
    const { id, listing } = params;

    try {
      let single: any | undefined;
      if (listing.length > 0) {
        single = listing.find((resource: any) => resource.id === id);
      } else {
        // Dynamic query - will be replaced with auto-generated query after schema push
        const messaging: any = await client.graphql({
          query: getMessaging,
          variables: { id },
          authMode: 'userPool',
        });
        single = messaging.data.getMessaging;
      }

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;
    // Handle both conceptID (from form) and conceptId (expected format)
    const conceptId = data.conceptId || data.conceptID;

    if (!conceptId) {
      const error = new Error(`Cannot create ${singleName} without conceptId`);
      return showError(error);
    }

    if (!data.trigger) {
      const error = new Error(`Cannot create ${singleName} without trigger`);
      return showError(error);
    }

    if (!data.channel) {
      const error = new Error(`Cannot create ${singleName} without channel`);
      return showError(error);
    }

    try {
      // Check if a messaging configuration with the same trigger already exists for this concept
      const existingMessaging = await fetch(
        {
          accountID: '',
          conceptID: conceptId,
          searchText: '',
          startIndex: 0,
          limit: 1000,
          moveForward: true,
        },
        true,
      );

      const duplicate = existingMessaging.find(
        (msg: any) =>
          msg.trigger === data.trigger &&
          msg.channel === data.channel &&
          msg.conceptId === conceptId &&
          msg.deleted === '0',
      );

      if (duplicate) {
        const error = new Error(
          `A messaging configuration with trigger "${data.trigger}" and channel "${data.channel}" already exists for this concept. Each trigger can only have one configuration per concept.`,
        );
        return showError(error);
      }

      const createInput: CreateMessagingInput = {
        conceptId: conceptId,
        operaHotelId: data.operaHotelId || '',
        trigger: data.trigger,
        templateName: data.templateName || null,
        subject: data.subject || '',
        header: data.header || '',
        messageTemplate: data.messageTemplate,
        footer: data.footer || '',
        isActive: data.isActive !== undefined ? data.isActive : true,
        sendRatingAfter:
          data.sendRatingAfter !== undefined ? data.sendRatingAfter : false,
        // ratingLink: data.ratingLink || '',
        precedence: data.precedence ? parseInt(data.precedence) : 0,
        delayMinutes: data.delayMinutes ? parseInt(data.delayMinutes) : 0,
        description: data.description || '',
        channel: data.channel || '',
        deleted: '0',
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      // Dynamic mutation - will be replaced with auto-generated mutation after schema push
      const result = await client.graphql({
        query: createMessaging,
        variables: { input: createInput },
        authMode: 'userPool',
      });

      // Refresh the listing to include the new item
      await fetch(
        {
          accountID: '',
          conceptID: conceptId,
          searchText: '',
          startIndex: 0,
          limit: 1000,
          moveForward: true,
        },
        true,
      );

      showConfirm(`New ${singleName} has been created successfully`);
      return true; // Return true to indicate successful creation
    } catch (err) {
      showError(err);
      return false; // Return false to indicate failure
    }
  }

  async function update(params: any) {
    const { id, listing, data } = params;

    try {
      // Step#1: Data Validation
      if (!data.trigger) {
        const error = new Error(`Cannot update ${singleName} without trigger`);
        return showError(error);
      }

      if (!data.channel) {
        const error = new Error(`Cannot update ${singleName} without channel`);
        return showError(error);
      }

      // Step#2: Get old data
      const original: any = await get({ id, listing });
      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      const conceptId =
        data.conceptId !== undefined ? data.conceptId : original.conceptId;
      const trigger =
        data.trigger !== undefined ? data.trigger : original.trigger;
      const channel =
        data.channel !== undefined ? data.channel : original.channel;

      // If trigger is being changed, check for duplicates
      if (data.trigger !== undefined && data.trigger !== original.trigger) {
        const existingMessaging = await fetch(
          {
            accountID: '',
            conceptID: conceptId,
            searchText: '',
            startIndex: 0,
            limit: 1000,
            moveForward: true,
          },
          true,
        );

        const duplicate = existingMessaging.find(
          (msg: any) =>
            msg.id !== id &&
            msg.trigger === trigger &&
            msg.channel === channel &&
            msg.conceptId === conceptId &&
            msg.deleted === '0',
        );

        if (duplicate) {
          const error = new Error(
            `A messaging configuration with trigger "${trigger}" and channel "${channel}" already exists for this concept. Each trigger can only have one configuration per concept.`,
          );
          return showError(error);
        }
      }

      const updateInput: UpdateMessagingInput = {
        id: original.id,
        conceptId: conceptId,
        operaHotelId:
          data.operaHotelId !== undefined
            ? data.operaHotelId
            : original.operaHotelId,
        trigger: trigger,
        templateName:
          data.templateName !== undefined
            ? data.templateName
            : original.templateName,
        subject: data.subject !== undefined ? data.subject : original.subject,
        header: data.header !== undefined ? data.header : original.header,
        messageTemplate:
          data.messageTemplate !== undefined
            ? data.messageTemplate
            : original.messageTemplate,
        footer: data.footer !== undefined ? data.footer : original.footer,
        isActive:
          data.isActive !== undefined ? data.isActive : original.isActive,
        sendRatingAfter:
          data.sendRatingAfter !== undefined
            ? data.sendRatingAfter
            : original.sendRatingAfter,
        // ratingLink:
        //   data.ratingLink !== undefined ? data.ratingLink : original.ratingLink,
        precedence:
          data.precedence !== undefined
            ? parseInt(data.precedence)
            : original.precedence,
        delayMinutes:
          data.delayMinutes !== undefined
            ? parseInt(data.delayMinutes)
            : original.delayMinutes,
        description:
          data.description !== undefined
            ? data.description
            : original.description,
        channel: channel,
      };

      // Dynamic mutation - will be replaced with auto-generated mutation after schema push
      await client.graphql({
        query: updateMessaging,
        variables: { input: updateInput },
        authMode: 'userPool',
      });

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: ConceptGetVariables) {
    try {
      const { id, listing } = params;

      const original: any = await get(params);

      if (original) {
        const updateInput: any = {
          id: original.id,
          deleted: '1',
        };

        // Dynamic mutation - will be replaced with auto-generated mutation after schema push
        await client.graphql({
          query: `
            mutation UpdateMessaging($input: UpdateMessagingInput!) {
              updateMessaging(input: $input) {
                id
                deleted
                __typename
              }
            }
          `,
          variables: { input: updateInput },
          authMode: 'userPool',
        });

        dispatch(
          setListing(listing.filter((resource: any) => resource.id !== id)),
        );
      }

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function remove(params: ConceptGetVariables) {
    const { id, listing } = params;
    try {
      // Dynamic mutation - will be replaced with auto-generated mutation after schema push
      await client.graphql({
        query: `
          mutation DeleteMessaging($input: DeleteMessagingInput!) {
            deleteMessaging(input: $input) {
              id
              __typename
            }
          }
        `,
        variables: { input: { id } },
        authMode: 'userPool',
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id)),
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  async function exportAll(params: ConceptListingVariables) {
    const data = await fetch(params);
    return data;
  }

  /**
   * Get Resource Name
   *
   * @param id id: string
   *
   * @returns string
   */
  const getName = (params: ConceptGetVariables) => {
    const { id, listing } = params;

    if (listing.length > 0) {
      const model = listing.find((model: any) => model.id === id);
      return model ? model.messageTemplate || model.trigger : '';
    }

    return '';
  };

  function options(listing: any[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({
        label: `${option.trigger} - ${
          option.messageTemplate?.substring(0, 30) || ''
        }`,
        value: option.id,
      });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'trigger',
      numeric: false,
      disablePadding: false,
      label: 'Trigger',
    },
    {
      id: 'channel',
      numeric: false,
      disablePadding: false,
      label: 'Channel',
    },
    {
      id: 'subject',
      numeric: false,
      disablePadding: false,
      label: 'Subject',
    },
    {
      id: 'messageTemplate',
      numeric: false,
      disablePadding: false,
      label: 'Message Template',
    },
    {
      id: 'isActive',
      numeric: false,
      disablePadding: false,
      label: 'Active',
    },
    {
      id: 'sendRatingAfter',
      numeric: false,
      disablePadding: false,
      label: 'Send Rating',
    },
    {
      id: 'precedence',
      numeric: false,
      disablePadding: false,
      label: 'Precedence',
    },
    {
      id: 'createdBy',
      numeric: false,
      disablePadding: false,
      label: 'Created By',
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Date',
    },
    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];

  const dataCells: readonly string[] = [
    'trigger',
    'channel',
    'subject',
    'messageTemplate',
    'isActive',
    'sendRatingAfter',
    'precedence',
  ];

  const api: any = {};

  // Subscription will be added after schema push
  // api[`${listingName}CreateSubscription`] = onCreateMessaging;

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}ChangeListing`] = (listing: any[]) => {
    dispatch(setListing(listing));
    dispatch(setFilters(listing.map((model: any) => model.trigger)));
  };

  api[`${listingName}ChangeSelected`] = (id: string) => {
    dispatch(setSelected(id));
  };
  api[`${listingName}ChangeFilters`] = (filters: string[]) =>
    dispatch(setFilters(filters));
  api[`${listingName}ChangeSelectedFilters`] = (filters: any) => {
    dispatch(setSelectedFilters(filters));
  };
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = listing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  api[`${listingName}PrevNextToken`] = undefined;

  return api;
};

export default useResource;
